import { GroupByMode, newTaskSuffix } from '@griply/types';
import { getNewTaskPrefill, onDayPartReorder } from '@griply/utils';
import { useUpdateTasks } from 'features/tasks';
import React, { useMemo, useRef } from 'react';
import { NewTaskRow } from 'shared/components/connected/new-task-row';
import {
  RenderItemProps,
  rootId,
  TaskListItemTypes,
  TreeItem,
  useTree,
  VirtualizedTreeList,
} from 'shared/components/ui/virtualized-tree-list';
import { useToday } from 'shared/contexts/today';
import { useTaskDateShortcuts } from 'shared/hooks/use-task-date-shortcuts';
import { useTaskPriorityShortcuts } from 'shared/hooks/use-task-priority-shortcuts';
import { ID } from 'shared/types/id';
import { DayPartSections } from 'shared/types/page-sections';
import { Task } from 'shared/types/task';

import { useDayPartTree } from '../../hooks/use-day-part-tree';
import { GroupedListProps } from '../../types';
import { DayPartHeader } from '../day-part-header';
import { TreeTaskCard } from '../tree-task-card';
import * as Styled from './day-part-grouping-tasks-list.style';

const taskItemTypes = [TaskListItemTypes.Task];

export const DayPartGroupingTasksList: React.FC<GroupedListProps> = ({
  tasks,
  sortingMode,
  onReorder,
  customOrder = {},
  collapsedSections = [],
  onSectionCollapse,
  weekStartsOn,
  scrollElement,
  denseFormat,
  groupSettings,
  isContextMenuOpen,
  defaultNewTaskDate,
}) => {
  const today = useToday();

  const tasksMap = useMemo(
    () =>
      tasks.reduce<Record<ID, Task>>((acc, task) => {
        acc[task.id] = task;
        return acc;
      }, {}),
    [tasks],
  );

  const treeData = useDayPartTree({
    tasks,
    customOrder,
    sortingMode,
    today,
    weekStartsOn,
    showEmptyGroups: groupSettings.showEmptyGroups,
    hideNewTaskRow: groupSettings.hideNewTaskRow,
  });

  const oldTreeData = useMemo(() => structuredClone(treeData), [treeData]);

  const _onReorder = (updatedItems: TreeItem[]) => {
    if (!onReorder) {
      return;
    }

    const result = onDayPartReorder({
      updatedItems,
      oldItems: Object.values(oldTreeData),
      tasksMap,
      today,
      weekStartsOn,
    });

    onReorder(result.updatedSections, result.updatedTasks);
  };

  // using refs to prevent the renderItem from rerendering all items
  const tasksMapRef = useRef(tasksMap);
  tasksMapRef.current = tasksMap;

  const tasksRef = useRef(tasks);
  tasksRef.current = tasks;

  const renderItem: React.FC<RenderItemProps> = useMemo(
    () =>
      ({ item, itemInstance, tree, isMultiSelectModifierPressed }) => {
        const type = item.type;

        // Task
        if (type === TaskListItemTypes.Task) {
          const currentTasksMap = tasksMapRef.current;
          const currentTasks = tasksRef.current;

          const parentElement = itemInstance.getParent();
          const parentSectionId = parentElement?.getItemData().sourceId;
          const displayDue =
            parentSectionId === DayPartSections.Overdue
              ? undefined
              : groupSettings.displayDue;

          return currentTasksMap[item.sourceId] ? (
            <TreeTaskCard
              task={currentTasksMap[item.sourceId]}
              tree={tree}
              item={itemInstance}
              tasks={currentTasks}
              isMultiSelectModifierPressed={isMultiSelectModifierPressed}
              displayDue={displayDue}
            />
          ) : null;
        }

        // New Task
        if (type === TaskListItemTypes.NewTask) {
          const groupSectionId = item.sourceId.replace(newTaskSuffix, '');
          const prefill = getNewTaskPrefill({
            groupByMode: GroupByMode.DayPart,
            groupSectionId,
            groupSetting: groupSettings,
            today,
            defaultNewTaskDate,
          });
          return (
            <NewTaskRow
              startTime={prefill.startTime ?? null}
              duration={null}
              date={prefill.startDate}
              lifeArea={prefill.lifeAreaId}
              tags={prefill.tagIds}
            />
          );
        }

        // DayPart
        if (type === TaskListItemTypes.DayPart) {
          return (
            <DayPartHeader denseFormat={denseFormat} item={itemInstance} />
          );
        }

        return null;
      },
    [denseFormat, defaultNewTaskDate, groupSettings, today],
  );

  const [tree] = useTree();
  const { submit: updateTasks } = useUpdateTasks();

  useTaskPriorityShortcuts(tree, tasks, updateTasks);
  useTaskDateShortcuts(tree, tasks, updateTasks);

  return (
    <Styled.Container>
      <VirtualizedTreeList
        rootItemId={rootId}
        items={treeData}
        renderItem={renderItem}
        scrollElement={scrollElement}
        multiselectTypes={taskItemTypes}
        dragTypes={taskItemTypes}
        onReorder={_onReorder}
        collapsedItems={collapsedSections}
        onCollapse={onSectionCollapse}
        denseFormat={denseFormat}
        isContextMenuOpen={isContextMenuOpen}
      />
    </Styled.Container>
  );
};
